<template>
  <div class="page-container-wrapper">
    <loader v-if="dataLoading"/>
    <template v-else>
      <div class="page-container-header d-flex align-items-center">
        <h2 class="text-md mb-0">{{ $t('message.Guideline.publications') }}</h2>

        <b-button variant="info" class="mr-2 ml-auto" @click="goBack">{{ $t('message.return') }}</b-button>
      </div>

      <div class="container-fluid">
        <div class="row">
          <!-- LEFT -->
          <div class="col-md-12 col-xl-7">
            <div class="page-container guidelines-editor" id="page-container">
              <b-alert v-show="dataError" class="mt-4" show variant="danger">{{ $t('message.connectionError') }}</b-alert>
              <div class="padding">

                <template v-if="isGuidelineSubmitter && guideline.phase == guidelinePhases.uploadReviewed">
                  <b-card :title="$t('message.Guideline.guidelineWasReviewed')" class="mb-2">
                    <router-link class="btn btn-primary btn text-center" :to="'/guidelines/' + guideline.id + '/upload'">
                      {{ $t('message.Guideline.uploadCorrectedFiles') }}
                    </router-link>
                  </b-card>
                </template>

                <div class="row">
                  <div class="col-md-12">
                    <b-card :title="$t('message.Guideline.sectionUploadedFiles')" class="mb-2 fileUploader">
                      <template v-if="guidelineFiles && guidelineFiles.length > 0">
                        <div class="table-responsive">
                          <b-table striped hover :items="guidelineFiles" :fields="['size', 'upload_id', 'name', 'type', 'actions']">
                            <template slot="info" slot-scope="data">
                              <pre>{{ data.item.response.data.id }}</pre>
                            </template>

                            <template slot="upload_id" slot-scope="data">
                              {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                            </template>

                            <template slot="progress" slot-scope="data">
                              <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                            </template>

                            <template slot="actions" slot-scope="data">
                              <a target="_blank" :href="data.item.url">
                                <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                              </a>
                            </template>
                          </b-table>
                        </div>
                      </template>

                      <button
                          class="btn btn-info"
                          @click="
                          prepareZippedFiles(
                            guideline.submitter_id,
                            guideline.upload_id
                          )
                        "
                      >
                        {{ $t("message.Guideline.downloadZipped") }}
                      </button>
                    </b-card>

                    <!-- LEFT BASE DATA -->
                    <base-data-left
                        :guideline="guideline"
                        class="d-xl-none"
                    ></base-data-left>

                    <!-- LEFT PROGRESS -->
                    <phase-preview-left
                        :guideline="guideline"
                        class="d-xl-none"
                    ></phase-preview-left>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12">
                    <h2>
                      {{ $t("message.Guideline.sectionFinalPublication") }}
                    </h2>

                    <b-card
                        :title="$t('message.Guideline.finalPublicationData')"
                        class="mb-0"
                    >
                      <b-row class="mb-3">
                        <b-col sm="5">
                          {{ $t("message.Guideline.dateOfPublication") }}
                          <var class="text-danger">*</var>
                        </b-col>
                        <b-col sm="7">
                          <datepicker
                              class="w-100"
                              :value="date_of_publication"
                              v-model="date_of_publication"
                              format="dd-MM-yyyy"
                              input-class="form-control"
                          >
                          </datepicker>

                          <input
                              type="hidden"
                              v-model="date_of_publication"
                              v-validate="'required'"
                              data-vv-name="date_of_publication"
                          />
                          <p
                              v-show="errors.has('date_of_publication')"
                              class="text-danger"
                          >
                            {{ $t("message.chooseProperDate") }}
                          </p>
                        </b-col>
                      </b-row>

                      <b-row class="mb-3">
                        <b-col sm="5">
                          {{ $t("message.Guideline.nameOfJournal") }}
                          <var class="text-danger">*</var>
                        </b-col>
                        <b-col sm="7">
                          <b-form-input
                              v-model="guideline.name_of_journal"
                              type="text"
                              v-validate="'required'"
                              data-vv-name="name_of_journal"
                              :state="
                              errors.has('name_of_journal') ? false : null
                            "
                          ></b-form-input>
                        </b-col>
                      </b-row>

                      <b-row class="mb-0">
                        <b-col sm="5">
                          {{ $t("message.Guideline.doiNumber") }}
                          <var class="text-danger">*</var>
                        </b-col>
                        <b-col sm="7">
                          <b-form-input
                              v-model="guideline.doi_number"
                              type="text"
                              v-validate="'required'"
                              data-vv-name="doi_number"
                              :state="errors.has('doi_number') ? false : null"
                          ></b-form-input>
                        </b-col>
                      </b-row>
                    </b-card>
                    <b-button variant="primary" @click="finalPublication">
                      {{ $t("message.save") }}
                    </b-button
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="d-md-none d-xl-block col-xl-5">
            <base-data-right :guideline="guideline"></base-data-right>

            <phase-preview :guideline="guideline"></phase-preview>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  GUIDELINE_REQUEST_BY_ID,
  GUIDELINE_FINAL_PUBLICATION,
} from "@/store/actions/guidelines";

import {Guideline, GuidelinePhases} from "@/lib/guideline.js";

import FileUpload from "vue-upload-component/dist/vue-upload-component.part.js";

import BaseDataRight from "@/components/Guideline/BaseData/right.vue";
import BaseDataLeft from "@/components/Guideline/BaseData/left.vue";

import PhasePreview from "@/components/Guideline/PhasePreview";
import PhasePreviewLeft from "@/components/Guideline/PhasePreview/left.vue";
import Api from "@/services/Api";

export default {
  name: "guideline-final-publication",
  components: {
    FileUpload,
    BaseDataRight,
    BaseDataLeft,
    PhasePreview,
    PhasePreviewLeft,
  },
  props: [],
  data() {
    return {
      guideline: new Guideline(),
      guidelinePhases: GuidelinePhases,
      files: [],
      date_of_publication: null,
      // name_of_journal: null,
      // publication_number: null,
      // issue_number: null,
      // doi_number: null,
    };
  },
  computed: {
    chunk() {
      return {
        headers: {Authorization: "Bearer " + this.getAuthToken},
        action: this.uploadChunkedUrl,
        maxActive: 1,
        maxRetries: 5,
      };
    },

    isGuidelineSubmitter() {
      return this.getUserData.id == this.guideline.submitter_id;
    },

    filesUploaded() {
      if (this.guideline && this.guideline.files) {
        return this.guideline.files.filter((elem) => {
          return (
              elem.upload_id != null &&
              elem.user_id == this.guideline.submitter_id
          );
        });
      }

      return [];
    },

    guidelineFiles() {
      if (this.guideline && this.guideline.files) {
        return this.guideline.files.filter((elem) => {
          return (
              elem.user_id == this.guideline.submitter_id &&
              elem.upload_id == this.guideline.upload_id
          );
        });
      }
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.$store.dispatch(GUIDELINE_REQUEST_BY_ID, this.$route.params.id).then((response) => {
        if (response.data.data.phase != this.guidelinePhases.boardApprovalApproved && !this.isSuperUser) {
          this.$router.push('/guidelines/' + response.data.data.id + '/view')
        }

        this.guideline = response.data.data;
        this.date_of_publication = this.moment(this.guideline.date_of_publication).format('DD-MM-YYYY');

        this.files = this.guideline.files.filter(elem => (elem.user_id == this.getUserData.id && elem.upload_id == null));

      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    goBack() {
      this.$router.push("/guidelines/");
    },

    prepareZippedFiles(userId, uploadId) {
      /**
       * Set as downloaded for user
       */
      let files = this.guideline.files.filter((row) => {
        return row.user_id == userId && row.upload_id == uploadId;
      });

      for (let x in files) {
        files[x].downloaded = true;
      }

      Api()
          .get(
              `/guidelines/prepare-zipped/${this.guideline.id}/${userId}/${uploadId}`
          )
          .then((response) => {
            window.open(response.data.fileUrl);
          })
          .catch((error) => {
            this.handleUnauth(error.response.status);
            this.handleForbidden(error.response.status);
          });
    },

    getZipped() {
      window.open(this.guideline.zipUrl);
    },

    getFileUrl(file) {
      return file.response && file.response.data != undefined
          ? file.response.data.url
          : file.url != undefined
              ? file.url
              : null;
    },

    finalPublication() {
      this.$validator.validate().then((result) => {
        if (!result) {
          this.$swal({
            title: "Error!",
            text: this.$t("message.Guideline.fillRequiredFields"),
            type: "error",
            confirmButtonText: "OK",
          });
          this.showValidateErrors = true;
        } else {
          let date_of_publication = (this.moment(this.date_of_publication, 'DD-MM-YYYY')).format('DD-MM-YYYY');
          let args = {
            date_of_publication: date_of_publication,
            name_of_journal: this.guideline.name_of_journal,
            publication_number: this.guideline.publication_number,
            issue_number: this.guideline.issue_number,
            doi_number: this.guideline.doi_number,
            id: this.guideline.id,
          };

          this.$store
              .dispatch(GUIDELINE_FINAL_PUBLICATION, args)
              .then(() => {
                this.$swal({
                  title: this.$t("message.success"),
                  text: this.$t("message.success"),
                  type: "success",
                  confirmButtonText: "OK",
                }).then(() => {
                  this.$router.push("/guidelines/" + this.guideline.id + "/view");
                });
              })
              .catch((error) => {
                this.handleUnauth(error.response.status);
                this.handleForbidden(error.response.status);
              });
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
</style>

